import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "../components/Reveal";
import SafeImage from "../components/SafeImage";
import { FALLBACK_POSTS } from "../data/fallbackContent";

const FALLBACK_IMG = "https://images.pexels.com/photos/6927546/pexels-photo-6927546.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PAGE_SIZE = 12;

// Curated label themes mapped server-side to real blog categories
const THEMES = [
  "All",
  "Tax Updates",
  "GST",
  "Compliance",
  "Litigation",
  "Corporate Advisory",
  "NRI Taxation",
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
}

export default function Insights() {
  // Render fallback content immediately. Live feed replaces it once it resolves.
  const [items, setItems] = React.useState(FALLBACK_POSTS);
  const [allCats, setAllCats] = React.useState([]);
  const [archiveTotal, setArchiveTotal] = React.useState(FALLBACK_POSTS.length);
  const [filteredTotal, setFilteredTotal] = React.useState(FALLBACK_POSTS.length);
  const [filter, setFilter] = React.useState("All");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);

  const reqRef = React.useRef(0);

  React.useEffect(() => {
    const reqId = ++reqRef.current;
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(page * PAGE_SIZE),
    });
    if (search.trim()) params.set("search", search.trim());
    if (filter && filter !== "All") params.set("category", filter);

    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 7000);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogger/posts?${params.toString()}`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d) => {
        if (reqId !== reqRef.current) return;
        if (Array.isArray(d.items) && d.items.length) {
          setItems(d.items);
          setAllCats(Array.isArray(d.categories) ? d.categories : []);
          setArchiveTotal(d.archive_total || 0);
          setFilteredTotal(d.total || 0);
        } else if (search || (filter && filter !== "All")) {
          // Real (non-failing) empty result for a filtered query — show "no results"
          setItems([]);
          setFilteredTotal(0);
        }
      })
      .catch(() => { /* keep current items (fallback or last good) */ })
      .finally(() => clearTimeout(t));

    return () => { clearTimeout(t); ctrl.abort(); };
  }, [filter, search, page]);

  // Reset page when filter/search changes
  const onFilter = (c) => { setFilter(c); setPage(0); };
  const onSearch = (e) => { setSearch(e.target.value); setPage(0); };

  const pageCount = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE));
  const showingFrom = filteredTotal === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(filteredTotal, (page + 1) * PAGE_SIZE);

  return (
    <div data-testid="insights-page">
      <section className="bg-[#0A192F] text-white pt-44 pb-32 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-[#C5A059]/8 blur-[140px]" aria-hidden />
        <div className="container-x relative">
          <p className="eyebrow text-[#C5A059]">Knowledge Center</p>
          <h1 className="editorial-display text-[clamp(2.4rem,6.4vw,6.4rem)] mt-12 max-w-5xl leading-[1.04]">
            Articles, updates and <span className="italic text-[#C5A059]">professional viewpoints.</span>
          </h1>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-12 border-b border-[#0A192F]/10 pb-8">
            <div className="relative flex-1 max-w-xl">
              <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                type="search"
                value={search}
                onChange={onSearch}
                data-testid="insights-search"
                placeholder="Search the archive — e.g. GST, MSME, Section 43B, NRI"
                className="w-full bg-transparent border-0 border-b border-transparent focus:border-[#0A192F]/30 pl-7 pr-2 py-3 text-[#0A192F] focus:outline-none placeholder:text-[#94a3b8]"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              {THEMES.map((c) => {
                const isActive = filter === c;
                const slug = c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onFilter(c)}
                    data-testid={`filter-${slug}`}
                    className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors ${isActive ? "bg-[#0A192F] text-white" : "text-[#475569] hover:text-[#0A192F]"}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-[#64748B] mb-12">
            <p>
              Showing <span className="text-[#0A192F]">{showingFrom}–{showingTo}</span> of <span className="text-[#0A192F]">{filteredTotal}</span> {filter !== "All" || search ? "matching" : "archive"} posts
            </p>
            <p className="hidden md:block">{allCats.length} editorial labels · live from <span className="italic text-[#C5A059]">blog.nrk.info</span></p>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-32">
              <p className="font-serif text-2xl text-[#0A192F]">No insights match your search.</p>
              <button type="button" onClick={() => { setSearch(""); setFilter("All"); setPage(0); }} className="btn-ghost-dark mt-8 inline-flex">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
              {items.map((p, i) => (
                <Reveal key={p.slug || p.id} delay={i * 50}>
                  <Link to={`/insights/${p.slug}`} data-testid={`insight-${p.slug}`} className="group block">
                    <div className="overflow-hidden aspect-[4/3] bg-[#FBFAF6]">
                      <SafeImage src={p.image || FALLBACK_IMG} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-[1200ms] grayscale-[10%]" loading="lazy" />
                    </div>
                    <div className="pt-7">
                      <p className="overline text-[#C5A059] text-[10px]">{p.category || "Insights"} · {p.read_time || "Read"}</p>
                      <h3 className="font-serif text-2xl lg:text-[1.7rem] text-[#0A192F] mt-5 leading-[1.15] group-hover:text-[#C5A059] transition-colors">{p.title}</h3>
                      <p className="text-[#475569] text-sm mt-5 leading-[1.7] line-clamp-3">{p.excerpt}</p>
                      <p className="overline text-[#64748B] text-[10px] mt-7 inline-flex items-center gap-2 group-hover:text-[#C5A059] transition-colors">{fmtDate(p.published)} <ArrowUpRight size={12} /></p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pageCount > 1 && (
            <div data-testid="insights-pagination" className="mt-20 lg:mt-28 pt-10 border-t border-[#0A192F]/10 flex flex-col md:flex-row items-center justify-between gap-6">
              <p className="text-xs text-[#64748B]">Page <span className="text-[#0A192F] font-serif text-base mx-1">{page + 1}</span> of <span className="text-[#0A192F] font-serif text-base mx-1">{pageCount}</span></p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => { setPage((p) => Math.max(0, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid="insights-prev"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#0A192F]/20 text-[#0A192F] text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:border-[#0A192F] hover:enabled:bg-[#0A192F] hover:enabled:text-white"
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <div className="hidden md:flex items-center gap-1 mx-2">
                  {Array.from({ length: pageCount }, (_, i) => i)
                    .filter((i) => i === 0 || i === pageCount - 1 || Math.abs(i - page) <= 1)
                    .reduce((acc, i, idx, arr) => {
                      if (idx > 0 && i - arr[idx - 1] > 1) acc.push("…");
                      acc.push(i);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "…" ? (
                        <span key={`gap-${idx}`} className="text-[#64748B] px-2">…</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          data-testid={`insights-page-${p+1}`}
                          className={`w-9 h-9 inline-flex items-center justify-center text-sm transition-colors ${page === p ? "bg-[#0A192F] text-white" : "text-[#475569] hover:text-[#0A192F]"}`}
                        >
                          {p + 1}
                        </button>
                      )
                    )}
                </div>
                <button
                  type="button"
                  disabled={page >= pageCount - 1}
                  onClick={() => { setPage((p) => Math.min(pageCount - 1, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid="insights-next"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#0A192F]/20 text-[#0A192F] text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:border-[#0A192F] hover:enabled:bg-[#0A192F] hover:enabled:text-white"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
